import { LicenseInfo } from '@mui/x-license';
import { CacheProvider } from './shared/contexts/CacheContext';
import './App.css';
import { Box, Typography, CircularProgress, Button, Stack } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import React, { useState, useEffect, JSX } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { setAppTheme } from './shared/Settings/appTheme';
//import Scoring from './Live Scoring/LiveScoring';
import { sizeAdjust } from './shared/Settings/SizeStyles';
import { useCache } from './shared/contexts/CacheContext';
import { register } from './shared/utils/serviceWorkerRegistration';
import HomePage from './HomePage';
import TournamentBuilder from './Tournament Builder/TournamentBuilder';
import { useAuth } from "react-oidc-context";
import { TBProvider } from './Tournament Builder/Contexts/useTBStates';
import { useTBContext } from './Tournament Builder/Contexts/useTBStates';
//import { MomentCentral } from './Moment Central/MomentCentral';
import CreateApi from './shared/Settings/appSettings';
import { useTokenManager } from '@moment-golf/auth';

if (process.env.REACT_APP_MUI_X_LICENSE_KEY) {
  LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_X_LICENSE_KEY);
}

let drawerWidth = 0;
const paddingWidth = sizeAdjust(1).paddingWidth;

// Move these outside component to prevent recreation
const getInitialAppDims = () => ({
  width: window.innerWidth,
  height: window.innerHeight,
  appWidth: window.innerWidth,
  appHeight: window.innerHeight - paddingWidth,
  tempDrawer: window.innerWidth - paddingWidth > 900 ? false : true
});

const getInitialCompHeights = () => ({
  appBarHeight: 0,
  remainingHeight: 0,
  extraHeight: 0
});

// Move calculations outside component
const calculateDimensions = (isLandscape: boolean, appBarHeight: number) => {
  if (isLandscape && window.innerHeight < 900) {
    return {
      appDims: {
        width: window.innerWidth - 240,
        height: window.innerHeight - paddingWidth,
        appWidth: window.innerWidth - 240,
        appHeight: window.innerHeight - paddingWidth,
        tempDrawer: window.innerWidth - paddingWidth > 900 ? false : true
      },
      compHeights: {
        appBarHeight,
        remainingHeight: window.innerHeight - appBarHeight,
        extraHeight: 80
      }
    };
  }

  return {
    appDims: {
      width: window.innerWidth,
      height: window.innerHeight - paddingWidth,
      appWidth: window.innerWidth - paddingWidth > 900 ?
        window.innerWidth - drawerWidth : window.innerWidth,
      appHeight: window.innerHeight - paddingWidth,
      tempDrawer: window.innerWidth - paddingWidth > 900 ? false : true
    },
    compHeights: {
      appBarHeight,
      remainingHeight: window.innerHeight - appBarHeight,
      extraHeight: 0
    }
  };
};

const AppContent = ({ refreshRequest, userDataPassed }: { refreshRequest: boolean, userDataPassed: any }): JSX.Element => {

  const [isLoading, setIsLoading] = useState(true);
  const auth = useAuth();
  const { userData, setUserData } = useTBContext();

  const [forceState, setForceState] = useState(false);

  const [appDims, setAppDims] = useState(getInitialAppDims());

  const [compHeights, setRemainingHeight] = useState(getInitialCompHeights());

  const [factor, setFactor] = React.useState(window.innerWidth - paddingWidth > 900 ? 1 : 0.80);
  const [appTheme, setTheme] = React.useState(setAppTheme(factor));
  const [showUpdateAlert, setShowUpdateAlert] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);

  const signOutRedirect = () => {
    const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID || '';
    const logoutUri = window.location.origin;
    const cognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN || '';
    window.location.href = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(logoutUri)}`;
  };

  /* #region Call Backs */
  // Memoize expensive calculations
  const getAppDims = React.useCallback(() => {
    const appBar = document.getElementById('scoring-header');
    const appBarHeight = appBar ? appBar.offsetHeight : 0;
    const isLandscape = window.innerWidth > window.innerHeight;

    const { appDims: newAppDims, compHeights: newCompHeights } = calculateDimensions(isLandscape, appBarHeight);

    const newWidth = window.innerWidth;
    setFactor(newWidth - paddingWidth > 900 ? 1 : 0.80);

    setAppDims(newAppDims);
    setRemainingHeight(newCompHeights);
  }, []);

  useEffect(() => {
    setUserData(userDataPassed);
  }, [userDataPassed]);
  /* #endregion */

  // Runs once on component mount
  useEffect(() => {
    const timeDelay = setTimeout(() => {
      setIsLoading(false);
    }, 500); 

    register({
      onUpdate: (registration) => {
        setShowUpdateAlert(true);
        setWaitingWorker(registration.waiting);
      }
    });

    /* setIsLoading(false); */

    // Cleanup the event listener on component unmount
  }, []);   

 /*  useEffect(() => {
    if (auth.isAuthenticated && !auth.isLoading) {
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    }
  }, [auth.isAuthenticated, auth.isLoading]); */

  
  // Memoize score props
  const scoreProps ={
    factor,
    appTheme,
    setTheme,
    setFactor,
    refreshRequest,
    forceState,
    setForceState,
    //currentUser,
    //userAttributes,
    paddingWidth,
    appDims,
  };

  const tbProps = {
    userData: userData
  };


  return (
    <CacheProvider>
      <ThemeProvider theme={appTheme}>
      <Box sx={{ 
          display: 'flex', 
          flex: 1, 
          overflow: 'hidden',
          height: '100dvh',  // Use dynamic viewport height
          width: '100%',
          position: 'fixed', // Fix the position to prevent scrolling
          top: 0,
          left: 0,
          WebkitOverflowScrolling: 'touch',
          WebkitOverscrollBehavior: 'none',
          overscrollBehavior: 'none',
          touchAction: 'pan-y'  // Changed from 'none' to 'pan-y'
        }}>
          
            <Box 
              sx={{ 
                display: isLoading ? 'flex' : 'none', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '100dvh',
                width: '100dvw',
                transition: 'opacity 0.5s ease-in-out',
                opacity: isLoading ? 1 : 0,
                pointerEvents: isLoading ? 'auto' : 'none'
              }}
              className={isLoading ? 'loading-transition' : 'loading-hidden'}
            >
              <Typography variant="h6">Loading...</Typography>
              <CircularProgress />
            </Box>
          
            { auth.isAuthenticated && !auth.isLoading && 
              userData?.accountTier && userData?.accountTier > 1 && (
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path='/TournamentBuilder' element={
                      <TournamentBuilder props={tbProps} />
                  } />
                 {/*  <Route path='/LiveScoring' element={
                    <Scoring key={forceState.toString()} props={scoreProps}></Scoring>
                  } /> */}
                </Routes>
            )} 
            { auth.isAuthenticated && !auth.isLoading && 
              (!userData?.accountTier || userData?.accountTier <= 1) && (
              <Stack direction='column' spacing={2} justifyContent='center' alignItems='center' height='100%' width='100%'>
                <Typography variant="h6">You are not authorized to access this application.</Typography>
                <Button onClick={() => {
                  auth.signoutSilent();
                  signOutRedirect();
                }}>Sign out</Button>
              </Stack>
            )}
        </Box>
      </ThemeProvider>
    </CacheProvider>
  );
};

// Simplify main App component to avoid duplication
function App() {

  const auth = useAuth();
  const api = CreateApi();
  const [userData, setUserData] = useState<any | null>(null);

  useEffect(() => {

    if (auth.isAuthenticated && userData === null) {
      const userID = auth.user?.profile?.sub;
      const getUserData = async () => {
        const userData = await api.get(`/tb/GetUser?userID=${userID}`);
        setUserData(userData.data);
      }

      getUserData();
    }

  // useTokenManager handles the interval below
  }, [auth.isAuthenticated]);

  useTokenManager(auth, { startupSilentSignIn: 'always' });



  return (
    <CacheProvider>
      <TBProvider>
        <Router>
          {auth.isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
              <CircularProgress />
            </div>
          ) : auth.isAuthenticated ? (
            <AppContent refreshRequest={true} userDataPassed={userData} />
          ) : (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
              <Button variant="contained" onClick={() => auth.signinRedirect()}>Sign in</Button>
            </div>
          )}
        </Router>
      </TBProvider>
    </CacheProvider>
  );
}
export default App;
